import { ContentBanner } from '@modernhealth/vital-components';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntl } from 'react-intl';

import { BackButton } from '../components/BackButton';
import { CircleOnDemandCard, CircleOnDemandCardSkeleton } from '../components/CircleOnDemandCard';
import { CircleOnDemandPageSection } from '../components/CircleOnDemandPageSection';
import { Page } from '../components/PageLayout';
import { useLanguageCode } from '../hooks/useLanguageCode';
import { useGetOnDemandCirclesQuery } from '../documents/getOnDemandCircles.generated';

const languageCodes = ['en', 'es', 'ja', 'hi'];

export const CirclesOnDemandOverviewPage = () => {
    const { t } = useTranslation('circles');

    return (
        <Page.Container>
            <Page.Header>
                <BackButton />
            </Page.Header>

            <Page.Content
                css={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 32,
                }}
            >
                <ContentBanner
                    title={t('pages.on_demand.hero.title')}
                    description={t('pages.on_demand.hero.description')}
                    theme="dark"
                    css={{
                        border: 'none',
                        background: 'linear-gradient(180deg, #DAF5FD 0%, #FFFFFF 100%)',
                    }}
                />

                <CirclesOnDemandNewSection />
                {languageCodes.map(languageCode => {
                    return (
                        <CirclesOnDemandByLanguageSection
                            key={languageCode}
                            languageCode={languageCode}
                        />
                    );
                })}
            </Page.Content>
        </Page.Container>
    );
};

function useTranslatedHeader(languageCode: string) {
    const { t } = useTranslation('circles');
    const intl = useIntl();
    const language = useMemo(() => {
        return intl.formatDisplayName(languageCode, {
            type: 'language',
        });
    }, [intl, languageCode]);

    return t('pages.on_demand.sections.language.title', { language });
}

const CirclesOnDemandByLanguageSectionSkeleton: React.FC<{ languageCode: string }> = ({
    languageCode,
}) => {
    const header = useTranslatedHeader(languageCode);

    return (
        <CircleOnDemandPageSection header={header}>
            <CircleOnDemandCardSkeleton />
            <CircleOnDemandCardSkeleton />
            <CircleOnDemandCardSkeleton />
        </CircleOnDemandPageSection>
    );
};

const CirclesOnDemandByLanguageSection: React.FC<{ languageCode: string }> = ({ languageCode }) => {
    const first = 10;
    const { data, loading } = useGetOnDemandCirclesQuery({
        variables: {
            first: 100,
        },
    });

    const header = useTranslatedHeader(languageCode);

    if (loading) {
        return <CirclesOnDemandByLanguageSectionSkeleton languageCode={languageCode} />;
    }

    if (data?.circleOnDemandConnection.__typename !== 'CircleOnDemandConnection') {
        return null;
    }

    const circles = data.circleOnDemandConnection.edges;
    if (!circles) return null;

    const edges = circles.filter(edge => edge?.node.languageCode === languageCode).slice(0, first);

    return (
        <CircleOnDemandPageSection header={header}>
            {edges.map((edge, i) => {
                const ondemand = edge?.node;
                if (!ondemand) return null;

                return (
                    <CircleOnDemandCard
                        key={`ondemand-${ondemand.id}-${i}`}
                        seconds={ondemand.video.durationInSeconds}
                        host={ondemand.facilitators[0]?.name}
                        languageCode={ondemand.languageCode}
                        thumb={ondemand.video.thumbnail}
                        name={ondemand.name}
                        slug={ondemand.slug}
                    />
                );
            })}
        </CircleOnDemandPageSection>
    );
};

const CirclesOnDemandNewSectionContainer: React.FC = ({ children }) => {
    const { t } = useTranslation('circles');

    return (
        <CircleOnDemandPageSection header={t('pages.on_demand.sections.new.title')}>
            {children}
        </CircleOnDemandPageSection>
    );
};

const CirclesOnDemandNewSection = () => {
    const languageCode = useLanguageCode();
    const first = 3;
    const { data, loading } = useGetOnDemandCirclesQuery({
        variables: {
            first: 100,
        },
    });

    if (loading) {
        return <CirclesOnDemandNewSectionSkeleton />;
    }

    if (data?.circleOnDemandConnection.__typename !== 'CircleOnDemandConnection') {
        return null;
    }

    const edges = data.circleOnDemandConnection.edges
        .filter(edge => edge?.node.languageCode === languageCode)
        .slice(0, first);

    return (
        <CirclesOnDemandNewSectionContainer>
            {edges.map((edge, i) => {
                const ondemand = edge?.node;
                if (!ondemand) return null;

                return (
                    <CircleOnDemandCard
                        seconds={ondemand.video.durationInSeconds}
                        host={ondemand.facilitators[0]?.name}
                        languageCode={ondemand.languageCode}
                        thumb={ondemand.video.thumbnail}
                        key={`${ondemand.id}-${i}`}
                        name={ondemand.name}
                        slug={ondemand.slug}
                    />
                );
            })}
        </CirclesOnDemandNewSectionContainer>
    );
};

const CirclesOnDemandNewSectionSkeleton = () => {
    return (
        <CirclesOnDemandNewSectionContainer>
            <CircleOnDemandCardSkeleton />
            <CircleOnDemandCardSkeleton />
            <CircleOnDemandCardSkeleton />
        </CirclesOnDemandNewSectionContainer>
    );
};
